import { create } from 'zustand'

import { HeaderPage } from 'pages/exploreLearnings/constants/header.constants'

interface SearchHistoryItem {
  text: string
  skill: string | null
  type?: 'skills' | 'resources' | 'community' | 'practitioners' | null
}

interface ExploreLearningsState {
  searchText: string
  search: string
  OU: string[]
  location: string[]
  skill: string[]
  skillNames: string[]
  sort: string
  resources: string
  onboarding: string
  searchHistory: SearchHistoryItem[]
  tabResults: {
    skills: { count: number; loading: boolean }
    resources: { count: number; loading: boolean }
    community: { count: number; loading: boolean }
  }
}

const getStoredSearchHistory = () => {
  const stored = localStorage.getItem('explore-learnings-search-history')
  if (!stored) return []

  try {
    const parsed = JSON.parse(stored)

    if (Date.now() > parsed.expiry) {
      localStorage.removeItem('explore-learnings-search-history')
      return []
    }

    return parsed.value || []
  } catch {
    return []
  }
}

export const useExploreLearnings = create<ExploreLearningsState>(() => ({
  searchText: '',
  searchHistory: getStoredSearchHistory(),
  search: '',
  OU: [],
  location: [],
  skill: [],
  skillNames: [],
  sort: 'a-z',
  resources: '',
  onboarding: 'false',
  tabResults: {
    skills: { count: 0, loading: false },
    resources: { count: 0, loading: false },
    community: { count: 0, loading: false },
  },
}))

const defaultState = {
  search: '',
  OU: [],
  location: [],
  skill: [],
  skillNames: [],
  sort: 'a-z',
  resources: '',
}

export const setExploreLearningsSearchText = (searchText: string) => {
  useExploreLearnings.setState({ searchText })
}

export const setExploreLearningsSearch = (search: string) => {
  useExploreLearnings.setState({ search })
}

export const setExploreLearningsOU = (OU: string[]) => {
  useExploreLearnings.setState({ OU })
}

export const setExploreLearningsLocation = (location: string[]) => {
  useExploreLearnings.setState({ location })
}

export const setExploreLearningsSkill = (skill: string[]) => {
  useExploreLearnings.setState({ skill })
}

export const setExploreLearningsSkillNames = (skillNames: string[]) => {
  useExploreLearnings.setState({ skillNames })
}

export const setExploreLearningsSort = (sort: string) => {
  useExploreLearnings.setState({ sort })
}

export const setExploreLearningsResources = (resources: string) => {
  useExploreLearnings.setState({ resources })
}

export const setExploreLearningsSearchHistory = (searchHistory: SearchHistoryItem[]) => {
  useExploreLearnings.setState({ searchHistory })

  const data = {
    value: searchHistory,
    expiry: Date.now() + 365 * 24 * 60 * 60 * 1000, // 1 year
  }

  localStorage.setItem('explore-learnings-search-history', JSON.stringify(data))
}

export const setExploreLearningsOnboarding = (onboarding: string) => {
  useExploreLearnings.setState({ onboarding })
}

export const resetFilters = () => {
  useExploreLearnings.setState(defaultState)
}

export const setTabResult = (tab: HeaderPage, count: number, loading: boolean) => {
  useExploreLearnings.setState(state => ({
    tabResults: {
      ...state.tabResults,
      [tab]: { count, loading },
    },
  }))
}
